import React, { useEffect, useRef, useState } from 'react';

// Rendering is explicit: a successful fetch or an opened tab is not a review.
export default function DocumentPreview({ item, loadFile, onReviewed, onClose }) {
  const [pdf, setPdf] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [error, setError] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [visited, setVisited] = useState(new Set());
  const [ready, setReady] = useState(false);
  const canvas = useRef(null);

  useEffect(()=>{
    let cancelled=false, objectUrl='', loadingTask;
    (async()=>{
      try {
        const blob=await loadFile(item);
        if(cancelled) return;
        if(blob.type==='application/pdf') {
          const engine=await import('pdfjs-dist/webpack.mjs');
          if(cancelled) return;
          loadingTask=engine.getDocument({data:new Uint8Array(await blob.arrayBuffer()),isEvalSupported:false});
          const document=await loadingTask.promise;
          if(!cancelled) setPdf(document);
        } else if(['image/png','image/jpeg'].includes(blob.type)) {
          objectUrl=URL.createObjectURL(blob);
          setImageUrl(objectUrl);
        } else throw new Error('This file is not a supported PDF, PNG or JPEG document.');
      } catch(e) { if(!cancelled) setError(e.message || 'Document could not be opened.'); }
    })();
    return ()=>{cancelled=true; if(objectUrl) URL.revokeObjectURL(objectUrl); if(loadingTask) loadingTask.destroy();};
  },[item,loadFile]);

  useEffect(()=>{
    if(!pdf) return;
    let cancelled=false, renderTask;
    setReady(false);
    (async()=>{
      try {
        const page=await pdf.getPage(pageNumber);
        if(cancelled) return;
        const viewport=page.getViewport({scale:1.4});
        const target=canvas.current;
        target.width=Math.ceil(viewport.width); target.height=Math.ceil(viewport.height);
        renderTask=page.render({canvas:target,canvasContext:target.getContext('2d'),viewport});
        await renderTask.promise;
        if(!cancelled) {setReady(true);setVisited(previous=>new Set([...previous,pageNumber]));}
      } catch(e) {if(!cancelled) setError(e.message || 'PDF page could not be rendered.');}
    })();
    return ()=>{cancelled=true;renderTask?.cancel();};
  },[pdf,pageNumber]);

  const canReview=ready&&!error&&(!pdf||visited.size===pdf.numPages);
  return <div style={{position:'fixed',inset:0,zIndex:10010,background:'#0f172aaa',padding:16,display:'grid',placeItems:'center'}}>
    <section role="dialog" aria-modal="true" aria-labelledby="document-preview-title" style={{width:'min(1050px, 100%)',maxHeight:'94vh',overflow:'auto',background:'white',borderRadius:16,padding:20,boxSizing:'border-box'}}>
      <header style={{display:'flex',justifyContent:'space-between',gap:16}}><h2 id="document-preview-title">{item.fileName || item.label || 'Document preview'}</h2><button type="button" onClick={onClose}>Close document</button></header>
      {error&&<p role="alert">{error}</p>}
      {!ready&&!error&&<p role="status">Loading document…</p>}
      {pdf&&<><div style={{display:'flex',gap:12,alignItems:'center',marginBottom:12}}>
        <button type="button" disabled={pageNumber===1||!ready} onClick={()=>setPageNumber(n=>n-1)}>Previous page</button>
        <span>Page {pageNumber} of {pdf.numPages}</span>
        <button type="button" disabled={pageNumber===pdf.numPages||!ready} onClick={()=>setPageNumber(n=>n+1)}>Next page</button>
      </div><canvas ref={canvas} aria-label={`PDF page ${pageNumber}`} style={{width:'100%',height:'auto',border:'1px solid #e2e8f0'}} /></>}
      {imageUrl&&<img src={imageUrl} alt={item.label || 'Worker document'} onLoad={()=>setReady(true)} onError={()=>setError('Image could not be rendered.')} style={{display:'block',maxWidth:'100%',height:'auto',margin:'auto'}} />}
      {pdf&&visited.size<pdf.numPages&&<p>Open each page before marking this file reviewed.</p>}
      <button type="button" disabled={!canReview} onClick={()=>{onReviewed();onClose();}} style={{marginTop:16}}>I have reviewed this file</button>
    </section>
  </div>;
}
